import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { get, set } from '@totalsoft/change-tracking-react';
import { Grid } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { emptyArray, emptyString } from 'constants/defaultValues';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const CascoComponent = ({
  insuranceDataLens,
  periods,
  validationInsuranceData,
  currencies,
  onFinancingTypeChange,
  financingTypes,
  readOnly,
  hasExternalBeneficiary
}) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditInsuranceData = useAuthorization(AuthorizationCodes.hasRightToEditInsuranceData, documentTypeId, documentStateId);
  const { t } = useTranslation();
  const optionEqualsValueAutocomplete = useCallback((option, value) => option.id === value, []);
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Autocomplete
          id='Casco_FinancingType'
          label={t('Offer.Edit.InsuranceData.FinancingType')}
          fullWidth
          required
          isSearchable={true}
          isClearable={false}
          valueKey='name'
          labelKey='name'
          value={insuranceDataLens.financingType |> get}
          onChange={onFinancingTypeChange}
          options={financingTypes || emptyArray}
          disabled={readOnly({
            controlName: 'InsuranceData.FinancingType',
            hasAuthorization: hasRightToEditInsuranceData
          })}
          simpleValue={true}
          error={!isValid(validationInsuranceData.financingType)}
          helperText={getErrors(validationInsuranceData.financingType)}
        />
      </Grid>
      {hasExternalBeneficiary && hasRightToEditInsuranceData && (
        <Grid item xs={12} md={6}>
          <TextField
            id={'Casco_ExternalBeneficiary'}
            fullWidth
            value={(insuranceDataLens.externalBeneficiary |> get) || emptyString}
            onChange={insuranceDataLens.externalBeneficiary |> set}
            label={t('Offer.Edit.InsuranceData.Beneficiary')}
            disabled={false}
            required
            error={!isValid(validationInsuranceData.externalBeneficiary)}
            helperText={getErrors(validationInsuranceData.externalBeneficiary)}
          />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <DateTime
          id='Casco_StartDate'
          label={t('Offer.Edit.InsuranceData.StartDate')}
          onChange={insuranceDataLens.startDate |> set}
          value={insuranceDataLens.startDate |> get}
          isValidDate={date => date.isSameOrAfter(new Date(), 'day')}
          timeFormat={false}
          fullWidth
          required
          disabled={readOnly({
            controlName: 'InsuranceData.StartDate',
            hasAuthorization: hasRightToEditInsuranceData
          })}
          error={!isValid(validationInsuranceData.startDate)}
          helperText={getErrors(validationInsuranceData.startDate)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        {readOnly({
          controlName: 'InsuranceData.MonthNo',
          hasAuthorization: hasRightToEditInsuranceData
        }) ? (
          <TextField
            id={'Casco_MonthNo'}
            fullWidth
            value={insuranceDataLens.monthNo |> get}
            label={t('Offer.Edit.InsuranceData.MonthNo')}
            disabled={true}
          />
        ) : (
          <Autocomplete
            id='Casco_MonthNo'
            label={t('Offer.Edit.InsuranceData.MonthNo')}
            fullWidth
            required
            isSearchable={true}
            isClearable={false}
            valueKey='id'
            labelKey='id'
            value={insuranceDataLens.monthNo |> get}
            onChange={value => (insuranceDataLens.monthNo |> set)(value.id)}
            options={periods ? periods : emptyArray}
            isOptionEqualToValue={optionEqualsValueAutocomplete}
            error={!isValid(validationInsuranceData.monthNo)}
            helperText={getErrors(validationInsuranceData.monthNo)}
          />
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          id='Casco_Currency'
          label={t('Offer.Edit.InsuranceData.Currency')}
          fullWidth
          required
          isSearchable={false}
          isClearable={false}
          valueKey='id'
          labelKey='code'
          value={insuranceDataLens.currencyId |> get}
          onChange={insuranceDataLens.currencyId |> set}
          options={currencies ? currencies : emptyArray}
          disabled={readOnly({
            controlName: 'InsuranceData.Currency',
            hasAuthorization: hasRightToEditInsuranceData
          })}
          simpleValue={true}
          error={!isValid(validationInsuranceData.currencyId)}
          helperText={getErrors(validationInsuranceData.currencyId)}
        />
      </Grid>
    </Grid>
  );
};

CascoComponent.propTypes = {
  insuranceDataLens: PropTypes.object.isRequired,
  periods: PropTypes.array,
  currencies: PropTypes.array,
  validationInsuranceData: PropTypes.object,
  onFinancingTypeChange: PropTypes.func,
  financingTypes: PropTypes.array,
  readOnly: PropTypes.func.isRequired,
  hasExternalBeneficiary: PropTypes.bool.isRequired
};

export default CascoComponent;
